/**
 * @file This file contains the patient API functions.
 * @module api/patientApi
 * 
 * There are enpoints that get here from both the auth server and the healthcare server
 * most of these endpoint calls come out of the practitioner healthcare system but somce come from labiq sample management as well
 * 
 */

import { ENV } from '../config/runtimeEnv';
import { updateSessionUser } from "../utils/auth/sessionUser";

//Types
import { User } from "../types/admin/UserTypes";
import { Patient } from "../types/healthcare/PatientTypes";
import { MedicalHistory } from "../types/healthcare/MedicalHistoryTypes";
import { Test } from "../types/healthcare/TestTypes";
import { Appointment, AppointmentPolicy } from "../types/appointments/AppointmentTypes";
import { AssignedForm } from "./formApi";
import { parseUTCToLocal } from "../utils/userPrefs/DateTimeUtils";
import type { UserUuid } from "../types/identity";
import { http } from "../lib/http/client";

// auth's patient profile routes moved to healthcare at the same paths (contract §15).
const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;
const HEALTHCARE_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/////////////////////////////////////////// PROFILE ROUTES /////////////////////////////////////////////////////////

export const fetchPatientProfile = async (id: UserUuid): Promise<{ user: Patient }> => {
	const response = await http.get(`${API_BASE_URL}/patient/${id}`);
	return {
		user: response.data,
	};
};

export const updatePatientProfile = async (userData: User) => {
	const response = await http.put(`${API_BASE_URL}/user`, userData);

	if (response.status === 200) {
		updateSessionUser(userData);
	}

	return response.data;
};

export const fetchPatientMedicalHistories = async (patientId: UserUuid): Promise<MedicalHistory[]> => {
	const response = await http.get(`${HEALTHCARE_BASE_URL}/patient/${patientId}/medical-histories`);
	return response.data;
};

export const fetchPatientTests = async (patientId: UserUuid): Promise<Test[]> => {
	const response = await http.get(`${HEALTHCARE_BASE_URL}/patient/${patientId}/test-orders`);
	return response.data;
};

/**
 * The patient's own assigned forms.
 *
 * Each row carries `access_token` — healthcare's `FormsController::GetAssignedForms` shapes
 * this response with `makeVisible('access_token')`, because the dashboard's form modal has
 * no URL to read `?t=` from and must be handed the capability directly. That is a deliberate
 * per-endpoint exposure: the column stays `$hidden` on the model, so it does NOT appear in
 * the Forms In Tray dashboard or anywhere else an `AssignedForm` is serialised.
 */
export const getPatientAssignedForms = async (patientId: UserUuid): Promise<AssignedForm[]> => {
	const response = await http.get(`${HEALTHCARE_BASE_URL}/patient/${patientId}/forms`);
	return response.data;
};

/////////////////////////////////////////// PATIENT APPOINTMENT MANAGEMENT /////////////////////////////////////////////////////////

/**
 * Convert appointment data from backend (UTC) to local timezone for display
 */
const convertAppointmentFromBackend = (appointment: any): Appointment => {
	return {
		...appointment,
		// Convert UTC strings to Date objects in local timezone
		start_time: parseUTCToLocal(appointment.start_time),
		end_time: parseUTCToLocal(appointment.end_time),
		created_at: parseUTCToLocal(appointment.created_at) || new Date(),
		updated_at: parseUTCToLocal(appointment.updated_at) || new Date(),
	};
};

/**
 * Get all appointments for a patient
 */
export const fetchPatientAppointments = async (patientId: UserUuid): Promise<{
	data: Appointment[];
	policy?: AppointmentPolicy;
}> => {
	const response = await http.get(`${HEALTHCARE_BASE_URL}/patient/${patientId}/appointments`);

	// Convert all appointments from backend format to local timezone
	const appointments = response.data.data.map(convertAppointmentFromBackend);

	return {
		data: appointments,
		// The clinic's cancellation policy — a sibling of `data` (S6). Optional: an older
		// healthcare build simply omits it, and the callers fall back to their prior behaviour.
		policy: response.data.policy as AppointmentPolicy | undefined,
	};
};

/**
 * Get upcoming appointments for a patient
 */
export const fetchPatientUpcomingAppointments = async (patientId: UserUuid): Promise<{
	data: Appointment[];
	policy?: AppointmentPolicy;
}> => {
	const response = await http.get(`${HEALTHCARE_BASE_URL}/patient/${patientId}/appointments/upcoming`);

	// Convert all appointments from backend format to local timezone
	const appointments = response.data.data.map(convertAppointmentFromBackend);

	return {
		data: appointments,
		// The clinic's cancellation policy — a sibling of `data` (S6). Optional: an older
		// healthcare build simply omits it, and the callers fall back to their prior behaviour.
		policy: response.data.policy as AppointmentPolicy | undefined,
	};
};

/**
 * Get past appointments for a patient
 */
export const fetchPatientPastAppointments = async (patientId: UserUuid): Promise<{
	data: Appointment[];
	policy?: AppointmentPolicy;
}> => {
	const response = await http.get(`${HEALTHCARE_BASE_URL}/patient/${patientId}/appointments/past`);

	// Convert all appointments from backend format to local timezone
	const appointments = response.data.data.map(convertAppointmentFromBackend);

	return {
		data: appointments,
		// The clinic's cancellation policy — a sibling of `data` (S6). Optional: an older
		// healthcare build simply omits it, and the callers fall back to their prior behaviour.
		policy: response.data.policy as AppointmentPolicy | undefined,
	};
};

/**
 * Cancel a patient's appointment
 */
export const cancelPatientAppointment = async (appointmentId: number): Promise<{
	message: string;
}> => {
	const response = await http.put(`${HEALTHCARE_BASE_URL}/patient/appointments/${appointmentId}/cancel`, {});

	return {
		message: response.data.message,
	};
};

/**
 * Reschedule a patient's appointment
 */
export const reschedulePatientAppointment = async (appointmentId: number, newStartTime: Date, newDate: Date): Promise<{
	data: Appointment;
	message: string;
}> => {
	const response = await http.put(`${HEALTHCARE_BASE_URL}/patient/appointments/${appointmentId}/reschedule`, {
		new_start_time: newStartTime.toISOString(),
		new_date: newDate.toISOString(),
	});

	// Convert response back to local timezone
	const appointment = convertAppointmentFromBackend(response.data.data);

	return {
		data: appointment,
		message: response.data.message,
	};
};